import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, getDoc, doc, query, orderBy, limit } from 'firebase/firestore';
import { Plus, Search, Bell, User, TrendingUp, Clock, Star, Film, Flame, Award, Sparkles, Settings, ChevronDown, ChevronUp, Users, Tv, LogOut, ChevronLeft, ChevronRight } from 'lucide-react';
import DesktopFeed from '../components/DesktopFeed';
import FeedDesktop from '../components/FeedDesktop';
import SpielLogo from './SpielLogoWhite.svg';
import FriendsTabDesktop from '../components/profile/FriendsTabDesktop';
import ShowsTabDesktop from '../components/profile/ShowsTabDesktop';
import SettingsTab from '../components/profile/SettingsTab';
import ShowDesktop from '../components/shows/ShowDesktop';
import CreateShowModal from '../components/modals/CreateShowModal';
import DesktopNotificationsTab from '../components/navbar/DesktopNotificationsTab';
import CreateEpisodeModal from '../components/modals/CreateEpisodeModal';

const HomeDesktop = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [userData, setUserData] = useState(null);
  const [friends, setFriends] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  // Refs for scrolling show rows
  const continueCreatingRef = useRef(null);
  const topStreakShowsRef = useRef(null);
  const topRatedShowsRef = useRef(null);
  const newEpisodeShowsRef = useRef(null);
  const newReleaseShowsRef = useRef(null);
  const allShowsRef = useRef(null);
  
  // State for home data
  const [homeData, setHomeData] = useState({
    userShows: [],
    topStreakShows: [],
    topRatedShows: [],
    newEpisodeShows: [],
    newReleaseShows: [],
    allShows: [],
    featuredShow: null,
    loaded: false
  });
  
  const {
    userShows,
    topStreakShows,
    topRatedShows,
    newEpisodeShows,
    newReleaseShows,
    allShows,
    featuredShow,
    loaded
  } = homeData;

  const [selectedShowId, setSelectedShowId] = useState(null);
  const [showCreateShowModal, setShowCreateShowModal] = useState(false);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showCreateEpisodeModal, setShowCreateEpisodeModal] = useState(false);
  const [selectedShowForEpisode, setSelectedShowForEpisode] = useState(null);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);

  // First, add a new state to track animation states
  const [tabAnimationState, setTabAnimationState] = useState('closed'); // 'closed', 'opening', 'open', 'closing'

  // Update the animation styles for a clearer sequence
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Tab content animation */
      .tab-content-container {
        transition: transform 0.5s ease-out, opacity 0.5s ease-out;
        transform-origin: top center;
      }
      
      .tab-content-container.opening {
        transform: translateY(-20px);
        opacity: 0;
        animation: slideDown 0.5s ease-out forwards;
      }
      
      .tab-content-container.closing {
        transform: translateY(0);
        opacity: 1;
        animation: slideUp 0.5s ease-out forwards;
      }
      
      .tab-content-container.open {
        transform: translateY(0);
        opacity: 1;
      }
      
      .tab-content-container.closed {
        transform: translateY(-20px);
        opacity: 0;
        pointer-events: none;
      }
      
      /* Tab item animations */
      .tab-item {
        opacity: 0;
        transform: translateY(15px);
      }
      
      .tab-content-container.open .tab-item {
        opacity: 1;
        transform: translateY(0);
        transition: opacity 0.4s ease-out, transform 0.4s ease-out;
        transition-delay: calc(0.5s + (var(--item-index) * 0.08s));
      }
      
      @keyframes slideDown {
        0% {
          transform: translateY(-20px);
          opacity: 0;
        }
        100% {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes slideUp {
        0% {
          transform: translateY(0);
          opacity: 1;
        }
        100% {
          transform: translateY(-20px);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Update the animation timing in the useEffect
  useEffect(() => {
    let animationTimer;
    
    if (activeTab) {
      // Start opening animation
      setTabAnimationState('opening');
      
      // After animation completes, set to open state
      animationTimer = setTimeout(() => {
        setTabAnimationState('open');
      }, 500); // Increased from 300ms to 500ms
    } else if (tabAnimationState !== 'closed') {
      // Start closing animation
      setTabAnimationState('closing');
      
      // After animation completes, set to closed state
      animationTimer = setTimeout(() => {
        setTabAnimationState('closed');
      }, 500); // Increased from 300ms to 500ms
    }
    
    return () => {
      clearTimeout(animationTimer);
    };
  }, [activeTab]);

  // Update the handleTabClick function timing
  const handleTabClick = (tabName) => {
    if (activeTab === tabName) {
      // If clicking the active tab, start closing animation
      setTabAnimationState('closing');
      
      // After animation completes, close the tab
      setTimeout(() => {
        setActiveTab(null);
        setTabAnimationState('closed');
      }, 500); // Increased from 300ms to 500ms
    } else {
      // If opening a new tab or switching tabs
      if (activeTab) {
        // If a tab is already open, close it first with animation
        setTabAnimationState('closing');
        
        setTimeout(() => {
          setActiveTab(tabName);
          setTabAnimationState('opening');
          
          // Then set to open after animation completes
          setTimeout(() => {
            setTabAnimationState('open');
          }, 500); // Increased from 300ms to 500ms
        }, 500); // Increased from 300ms to 500ms
      } else {
        // If no tab is open, simply open the new tab with animation
        setActiveTab(tabName);
        setTabAnimationState('opening');
        
        setTimeout(() => {
          setTabAnimationState('open');
        }, 500); // Increased from 300ms to 500ms
      }
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        return;
      }

      try {
        // Fetch user data
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }

        // Fetch friends
        const friendsRef = collection(db, 'users', user.uid, 'friends');
        const friendsSnapshot = await getDocs(friendsRef);
        const friendsData = friendsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFriends(friendsData);

        // Fetch notifications
        const notificationsRef = collection(db, 'users', user.uid, 'notifications');
        const notificationsQuery = query(
          notificationsRef,
          orderBy('timestamp', 'desc'),
          limit(20)
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);
        const notificationsData = notificationsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Ensure timestamp is properly formatted
            timestamp: data.timestamp || new Date()
          };
        });
        console.log('Fetched notifications:', notificationsData);
        setNotifications(notificationsData);

      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };

    fetchUserData();
  }, [user]);

  useEffect(() => {
    const fetchShows = async () => {
      if (!user || loaded) {
        console.log('HomeDesktop: Skipping fetch, already loaded or no user');
        return;
      }
      
      console.log('HomeDesktop: Starting to fetch shows');
      
      try {
        setLoading(true);
        setError(null);
        
        // 1. Get user's friends
        const friendsRef = collection(db, 'users', user.uid, 'friends');
        const friendsSnapshot = await getDocs(friendsRef);
        const friendIds = friendsSnapshot.docs.map(doc => doc.data().friendUserId);
        
        // 2. Get user's own shows
        const userShowsRef = collection(db, 'users', user.uid, 'shows');
        const userShowsSnapshot = await getDocs(userShowsRef);
        const userShowIds = userShowsSnapshot.docs.map(doc => doc.id);
        
        // 3. Fetch full show details for user's shows
        const userShowsData = [];
        for (const showId of userShowIds) {
          const showDoc = await getDoc(doc(db, 'shows', showId));
          if (showDoc.exists()) {
            // Get episodes for this show to count them
            const episodesRef = collection(db, 'shows', showId, 'episodes');
            const episodesSnapshot = await getDocs(episodesRef);
            
            // Count episodes in the current season
            const currentSeason = showDoc.data().currentSeason || 1;
            const episodesInCurrentSeason = episodesSnapshot.docs.filter(
              doc => doc.data().episodeSeason === currentSeason
            ).length;
            
            userShowsData.push({
              id: showId,
              ...showDoc.data(),
              isUserShow: true,
              episodeCount: episodesInCurrentSeason
            });
          }
        }
        
        // 4. Get shows from friends
        const friendsShowsData = [];
        for (const friendId of friendIds) {
          // Get friend's shows
          const friendShowsRef = collection(db, 'users', friendId, 'shows');
          const friendShowsSnapshot = await getDocs(friendShowsRef);
          
          // Get friend's user info
          const friendDoc = await getDoc(doc(db, 'users', friendId));
          const friendData = friendDoc.exists() ? friendDoc.data() : { name: 'Unknown', handle: 'unknown' };
          
          // For each show, get the full details
          for (const showDoc of friendShowsSnapshot.docs) {
            const showId = showDoc.id;
            const fullShowDoc = await getDoc(doc(db, 'shows', showId));
            
            if (fullShowDoc.exists()) {
              friendsShowsData.push({
                id: showId,
                ...fullShowDoc.data(),
                friendName: friendData.name,
                friendHandle: friendData.handle,
                friendId: friendId
              });
            }
          }
        }
        
        // 5. Combine all shows for the feed
        const allShowsData = [...userShowsData, ...friendsShowsData];
        
        // 6. Set featured show (show with the most episodes)
        let featuredShowData = null;
        if (userShowsData.length > 0) {
          // Get all episodes for each show to find the one with the most episodes
          const showsWithEpisodeCounts = await Promise.all(
            userShowsData.map(async (show) => {
              const episodesRef = collection(db, 'shows', show.id, 'episodes');
              const episodesSnapshot = await getDocs(episodesRef);
              const episodeCount = episodesSnapshot.docs.length;
              
              return {
                ...show,
                totalEpisodeCount: episodeCount
              };
            })
          );
          
          // Sort by episode count (highest first)
          const sortedShows = [...showsWithEpisodeCounts].sort((a, b) => 
            (b.totalEpisodeCount || 0) - (a.totalEpisodeCount || 0)
          );
          
          // Select the show with the most episodes
          featuredShowData = sortedShows[0];
          
          console.log('Featured show selected:', featuredShowData.showName, 
            'with', featuredShowData.totalEpisodeCount, 'episodes');
        } else if (allShowsData.length > 0) {
          featuredShowData = allShowsData[0];
        }
        
        // Initial update with the correct featured show
        setHomeData({
          userShows: userShowsData,
          topStreakShows: [],
          topRatedShows: [],
          newEpisodeShows: [],
          newReleaseShows: [],
          allShows: allShowsData,
          featuredShow: featuredShowData,
          loaded: true
        });
        
        // 7. Process shows for different categories
        
        // Top streak shows
        const streakShows = [...allShowsData].sort((a, b) => 
          (b.showStreak || 0) - (a.showStreak || 0)
        ).slice(0, 10).map(show => ({
          ...show,
          streak: show.showStreak || 0
        }));
        
        // Top rated shows
        const ratedShows = [...allShowsData].sort((a, b) => {
          const scoreA = a.showEarnedScore / (a.showPossibleScore || 1);
          const scoreB = b.showEarnedScore / (b.showPossibleScore || 1);
          return scoreB - scoreA;
        }).slice(0, 10).map(show => ({
          ...show,
          score: (show.showEarnedScore / (show.showPossibleScore || 1)) * 10
        }));
        
        // Shows with new episodes (last 72 hours)
        const threeDaysAgo = new Date();
        threeDaysAgo.setHours(threeDaysAgo.getHours() - 72);
        
        const newEpisodesShows = allShowsData.filter(show => {
          // Check if the show has a latestEpisodeDate field and it's within the last 72 hours
          if (show.latestEpisodeDate) {
            const episodeDate = show.latestEpisodeDate.toDate ? 
              show.latestEpisodeDate.toDate() : new Date(show.latestEpisodeDate);
            return episodeDate >= threeDaysAgo;
          }
          return false;
        }).map(show => ({
          ...show,
          hasNewEpisodes: true
        }));
        
        // New releases (shows created in the last week)
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        
        const newReleasesShows = allShowsData.filter(show => {
          // Check if the show has a showCreationDate field and it's within the last week
          if (show.showCreationDate) {
            const creationDate = show.showCreationDate.toDate ? 
              show.showCreationDate.toDate() : new Date(show.showCreationDate);
            return creationDate >= oneWeekAgo;
          }
          return false;
        }).map(show => ({
          ...show,
          isNewRelease: true
        }));
        
        // Final update with all categories
        setHomeData({
          userShows: userShowsData,
          topStreakShows: streakShows,
          topRatedShows: ratedShows,
          newEpisodeShows: newEpisodesShows,
          newReleaseShows: newReleasesShows,
          allShows: allShowsData,
          featuredShow: featuredShowData,
          loaded: true
        });
        
        console.log('HomeDesktop: Successfully fetched shows');
        
      } catch (err) {
        console.error('Error fetching shows:', err);
        setError('Failed to load shows');
      } finally {
        setLoading(false);
      }
    };

    fetchShows();
  }, [user, loaded]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    try {
      setIsSearching(true);
      
      // Search only in the shows that the user has access to (own shows + friends' shows)
      const results = allShows.filter(show => 
        show.showName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (show.showDescription && show.showDescription.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching:', err);
    }
  };

  useEffect(() => {
    if (searchQuery) {
      const delaySearch = setTimeout(() => {
        handleSearch();
      }, 500);
      
      return () => clearTimeout(delaySearch);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [searchQuery, allShows]);

  // Update this useEffect to add the custom scrollbar styles
  useEffect(() => {
    // Create a style element for the placeholder, scrollbar, and loader
    const style = document.createElement('style');
    style.textContent = `
      .search-input::placeholder {
        color: rgba(255, 255, 255, 0.9);
      }
      
      /* Custom scrollbar styles */
      .search-results::-webkit-scrollbar {
        width: 8px;
      }
      
      .search-results::-webkit-scrollbar-track {
        background: rgba(51, 51, 51, 0.5);
        border-radius: 4px;
      }
      
      .search-results::-webkit-scrollbar-thumb {
        background: #6600CC;
        border-radius: 4px;
      }
      
      .search-results::-webkit-scrollbar-thumb:hover {
        background: #7700ee;
      }
      
      /* Hide default scrollbar for the main content */
      body {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
      }
      
      body::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
      }
      
      .main-content {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
      }
      
      .main-content::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
      }
      
      /* Show tile hover effects */
      .show-tile {
        transition: transform 0.3s ease, box-shadow 0.3s ease;
      }
      
      .show-tile:hover {
        transform: translateY(-5px) scale(1.03);
        box-shadow: 0 8px 20px rgba(102, 0, 204, 0.4);
        z-index: 5;
      }
      
      /* Continue creating tile hover effects */
      .create-tile {
        transition: transform 0.3s ease, box-shadow 0.3s ease;
      }
      
      .create-tile:hover {
        transform: translateY(-5px) scale(1.03);
        box-shadow: 0 8px 20px rgba(102, 0, 204, 0.6);
        z-index: 5;
      }
      
      /* Streak show tile hover effects - updated to scale only the image */
      .streak-tile {
        position: relative;
        z-index: 1;
      }
      
      .streak-tile .image-container {
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        border-radius: 0.75rem;
        overflow: visible;
      }
      
      .streak-tile:hover .image-container {
        transform: scale(1.05);
        box-shadow: 0 10px 25px rgba(102, 0, 204, 0.5);
        z-index: 5;
      }
      
      /* Rank indicator should not transform with the tile */
      .rank-indicator {
        position: absolute;
        z-index: 0;
      }
      
      .loader {
        position: relative;
        width: 100px;
        height: 100px;
      }

      .loader:before , .loader:after{
        content: '';
        border-radius: 50%;
        position: absolute;
        inset: 0;
        box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.3) inset;
      }
      .loader:after {
        box-shadow: 0 2px 0 #6600cc inset;
        animation: rotate 2s linear infinite;
      }

      @keyframes rotate {
        0% {  transform: rotate(0)}
        100% { transform: rotate(360deg)}
      }
    `;
    document.head.appendChild(style);
    
    // Clean up on unmount
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const scrollLeft = (ref) => {
    if (ref && ref.current) {
      const container = ref.current;
      const scrollAmount = 600; // Scroll by 3 items (200px each)
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };
  
  const scrollRight = (ref) => {
    if (ref && ref.current) {
      const container = ref.current;
      const scrollAmount = 600; // Scroll by 3 items (200px each)
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const renderShowRow = (title, shows, emptyMessage, scrollRef) => {
    return (
      <section style={{ marginBottom: '.5rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <h2 style={{ 
            margin: 0,
            fontSize: '1.2rem',
            fontWeight: '500',
            color: 'grey'
          }}>
            {title}
          </h2>
        </div>
        
        <div style={{ position: 'relative' }}>
          {/* Left scroll button */}
          <button 
            onClick={() => scrollLeft(scrollRef)}
            style={{
              position: 'absolute',
              left: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.7)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ChevronLeft size={24} color="white" />
          </button>
          
          {/* Shows container */}
          <div 
            ref={scrollRef}
            style={{
              display: 'flex',
              gap: '1.5rem',
              marginTop: '-2.5rem',

               height: '10rem',
              overflowX: 'hidden',
              paddingBottom: '1rem',
              scrollBehavior: 'smooth'
            }}
          >
            {shows.length > 0 ? (
              shows.map(show => (
                <div
                  key={show.id}
                  onClick={() => setSelectedShowId(show.id)}
                  className="show-tile"
                  style={{
                    width: '200px',
                    marginTop: '2rem',
                    flexShrink: 0,
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                >
                  <div style={{
                    width: '100%',
                    aspectRatio: '3/2',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {show.showImage ? (
                      <img
                        src={show.showImage}
                        alt={show.showName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999'
                      }}>
                        <Film size={32} />
                      </div>
                    )}
                    
                    {/* Overlay gradient */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.8) 100%)',
                      borderRadius: '0.5rem'
                    }} />
                    
                    {/* Rating (only for Critically Acclaimed row) */}
                    {title === 'Critically Acclaimed' && show.showPossibleScore > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        backgroundColor: 'rgba(55,55,55,0.9)',

                        color: 'white',
                        border: '1px solid grey',
                      
                        fontWeight: 'bold',
                        textAlign: "center",
                        fontSize: '.8rem', 


                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                       lineHeight: '.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
             
                      ISDb:  {((show.showEarnedScore / show.showPossibleScore) * 10).toFixed(1)}
                      </div>
                    )}
                    
                    {/* Tags */}
                    <div style={{
                      position: 'absolute',
                      top: '0.5rem',
                      left: '0.5rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      maxWidth: '90%'
                    }}>
                      {/* Streak tag - only in Top Shows by Streak row */}
                      {title === 'Top Shows by Streak' && show.showStreak > 0 && (
                        <span style={{
                          backgroundColor: '#6600CC',
                          color: 'white',
                          fontSize: '0.7rem',
                          padding: '0.2rem 0.4rem',
                          borderRadius: '0.25rem',
                          fontWeight: 'bold',
                          display: 'inline-block'
                        }}>
                          {show.showStreak} DAY STREAK
                        </span>
                      )}
                      
                      {/* New Episodes tag */}
                      {show.hasNewEpisodes && (
                        <span style={{
                          backgroundColor: '#6600CC',
                          color: 'white',
                          fontSize: '0.7rem',
                          padding: '0.2rem 0.4rem',
                          borderRadius: '0.25rem',
                          fontWeight: 'bold',
                          display: 'inline-block'
                        }}>
                          NEW EPISODES
                        </span>
                      )}
                      
                      {/* New Release tag - adjusted styling */}
                      {show.isNewRelease && (
                        <span style={{
                          backgroundColor: '#6600CC',
                          color: 'white',
                          fontSize: '0.7rem',
                          padding: '0.2rem 0.4rem',
                          borderRadius: show.hasNewEpisodes ? '0 0 0.25rem 0.25rem' : '0.25rem',
                          fontWeight: 'bold',
                          display: 'inline-block',
                          marginTop: show.hasNewEpisodes ? '-0.25rem' : '0',
                        }}>
                          NEW RELEASE
                        </span>
                      )}
                    </div>
                    
                    {/* Show title */}
                    <div style={{
                      position: 'absolute',
                      bottom: '0.5rem',
                      left: '0.5rem',
                      right: '0.5rem'
                    }}>
                      <h3 style={{
                        margin: '0',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: 'white',
                        textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                      }}>
                        {show.showName}
                      </h3>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{
                padding: '2rem',
                backgroundColor: '#1a1a1a',
                borderRadius: '0.5rem',
                color: '#999',
                textAlign: 'center',
                width: '100%'
              }}>
                {emptyMessage}
              </div>
            )}
          </div>
          
          {/* Right scroll button */}
          <button 
            onClick={() => scrollRight(scrollRef)}
            style={{
              position: 'absolute',
              right: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.7)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ChevronRight size={24} color="white" />
          </button>
        </div>
      </section>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'notifications':
        return (
          <DesktopNotificationsTab 
            notifications={notifications} 
            userId={user.uid}
            isInline={true}
          />
        );
      case 'friends':
        return (
          <FriendsTabDesktop 
            friends={friends} 
            onAddFriend={() => {
              // Your add friend logic
            }}
          />
        );
      case 'shows':
        return (
          <ShowsTabDesktop 
            userShows={userShows} 
            onCreateShow={() => setShowCreateShowModal(true)}
            user={user}
            onOpenShow={(showId) => setSelectedShowId(showId)}
          />
        );
      case 'settings':
        return <SettingsTab userData={userData} userId={user?.uid} />;
      default:
        return null;
    }
  };

  // Special rendering for Continue Creating row
  const renderContinueCreatingRow = (shows, scrollRef) => {
    return (
      <section style={{ marginBottom: '2.5rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <h2 style={{ 
            margin: 0,
            fontSize: '1.2rem',
            marginBottom:'-20px',
            fontWeight: '500', 
            color: 'grey'
          }}>
            Continue Creating
          </h2>
        </div>
        
        <div style={{ position: 'relative' }}>
          {/* Left scroll button */}
          <button 
            onClick={() => scrollLeft(scrollRef)}
            style={{
              position: 'absolute',
              left: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.7)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ChevronLeft size={24} color="white" />
          </button>
          
          {/* Shows container */}
          <div 
            ref={scrollRef}
            style={{
              display: 'flex',
              gap: '1.5rem',
              overflowX: 'hidden',
              paddingBottom: '1rem',
              scrollBehavior: 'smooth'
            }}
          >
           
            
            {/* Existing shows */}
            {shows.map(show => (
              <div
                key={show.id}
                onClick={() => setSelectedShowId(show.id)}
                className="create-tile"
                style={{
                  width: '200px', marginTop: '1.5rem',
                  flexShrink: 0,
                  cursor: 'pointer',
                  position: 'relative',
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                  backgroundColor: '#1a1a1a',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              >
                {/* Image container */}
                <div style={{
                  width: '100%',
                  aspectRatio: '3/2',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {show.showImage ? (
                    <img
                      src={show.showImage}
                      alt={show.showName}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999'
                    }}>
                      <Film size={32} />
                    </div>
                  )}
                  
                  {/* Plus circle overlay */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    border: '2px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.9,
                    transition: 'opacity 0.2s, transform 0.2s',
                    boxShadow: '0 2px 8px rgba(102, 0, 204, 0.8)'
                  }}>
                    <Plus size={30} color="white" />
                  </div>
                </div>
                
                {/* Show details */}
                <div style={{
                  padding: '0.75rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  backgroundColor: '#1a1a1a'
                }}>
                  <h3 style={{
                    margin: '0 0 0.25rem 0',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: 'white',
                    width: 'calc(100% - 2.5rem)',
                  }}>
                    {show.showName} 
                  </h3>
                  
                  <div style={{
                    display: 'flex',
                    fontSize: '0.8rem',
                    color: '#999'
                  }}>
                    <span>S{show.currentSeason || 1}:</span>
                    <span>E{show.episodeCount || 0}</span>
                  </div>
                </div>
              </div>
            ))}
             {/* Create New Show tile */}
             <div
              onClick={() => setShowCreateShowModal(true)}
              className="create-tile"
              style={{
                width: '200px',
                flexShrink: 0,
                cursor: 'pointer',
                position: 'relative',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                marginTop: '1.5rem',
                backgroundColor: '#6600cc',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            >
              {/* Image container */}
              <div style={{
                width: '100%',
                aspectRatio: '3/2',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: 'rgba(102, 0, 204, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Plus circle overlay */}
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: '#6600cc',
                  border: '2px solid white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.9,
                  transition: 'opacity 0.2s, transform 0.2s',
                  boxShadow: '0 2px 8px rgba(102, 0, 204, 0.8)'
                }}>
                  <Plus size={30} color="white" />
                </div>
              </div>
              
              {/* Show details */}
              <div style={{
                padding: '0.75rem',
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: '#6600cc',
              }}>
                <h3 style={{
                  margin: '0',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  Create New Show
                </h3>
              </div>
            </div>

          </div>
          
          {/* Right scroll button */}
          <button 
            onClick={() => scrollRight(scrollRef)}
            style={{
              position: 'absolute',
              right: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.7)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ChevronRight size={24} color="white" />
          </button>
        </div>
      </section>
    );
  };

  const handleShowCreated = (newShow) => {
    setShowCreateShowModal(false);
    
    // Update the homeData state to include the new show
    setHomeData(prevData => ({
      ...prevData,
      userShows: [newShow, ...prevData.userShows]
    }));
  };

  // Add a function to handle opening shows from the ShowsTab
  const handleOpenShowFromTab = (showId) => {
    setSelectedShowId(showId);
    setActiveTab(null); // Close the tab/sidebar
  };

  // Add a new function to render the streak shows row specifically
  const renderStreakShowsRow = (shows, scrollRef) => {
    return (
      <section style={{ marginBottom: '2.5rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <h2 style={{ 
            margin: 0,
            fontSize: '1.2rem',
            fontWeight: '500',
            color: 'grey'
          }}>
            Top Shows by Streak
          </h2>
        </div>
        
        <div style={{ position: 'relative' }}>
          {/* Left scroll button */}
          <button 
            onClick={() => scrollLeft(scrollRef)}
            style={{
              position: 'absolute',
              left: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.7)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ChevronLeft size={24} color="white" />
          </button>
          
          {/* Shows container */}
          <div 
            ref={scrollRef}
            style={{
              display: 'flex',
              gap: '1.5rem',
              overflowX: 'hidden',
              height: '350px', 
              marginTop: '-1rem',
              paddingBottom: '1rem',
              scrollBehavior: 'smooth',
              paddingLeft: '1rem',
              paddingRight: '1rem'
            }}
          >
            {shows.length > 0 ? (
              shows.map((show, index) => (
                <div
                  key={show.id}
                  onClick={() => setSelectedShowId(show.id)}
                 
                  style={{
                    width: '200px',
                    height: '300px',
                    flexShrink: 0,
                    marginLeft: '3rem',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                >
                  {/* Rank indicator */}
                  <div 
                    className="rank-indicator"
                    style={{
                      position: 'absolute',
                      top: 'calc(50% + 20px)',
                      transform:'translatey(-50%)',
                      left: '-2.7rem',
                      zIndex: 0,
                      color: '#6600CC',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '6.1rem',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    {index + 1}
                  </div>
                  
                  {/* Image container - full height with className for targeting in CSS */}
                  <div  className="streak-tile" style={{height: '300px', width: '200px', marginTop:'20px'}}>
                  <div 
                    className="image-container"
                    
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      borderRadius: '0.75rem',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                      overflow: 'hidden'
                    }}
                  >
                    {show.showImage ? (
                      <img
                        src={show.showImage}
                        alt={show.showName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '0.75rem'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999'
                      }}>
                        <Film size={32} />
                      </div>
                    )}
                    
                    {/* Gradient overlay */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.8) 100%)',
                      borderRadius: '0.75rem'
                    }} />
                    
                    {/* Streak badge */}
                    <div style={{
                      position: 'absolute',
                      top: '0.5rem',
                      left: '50%',
                      transform: 'translatex(-50%)',
                      backgroundColor: '#6600CC',
                      color: 'white',
                      fontSize: '0.8rem',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      {show.showStreak || 0} Day Streak 
                    </div>
                    
                    {/* Show info at bottom */}
                    <div style={{
                      position: 'absolute',
                      bottom: '1rem',
                      left: '1rem',
                      right: '1rem'
                    }}>
                      <h3 style={{
                        margin: '0 0 0.5rem 0',
                        fontSize: '1.5rem',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: 'white',
                        textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                      }}>
                        {show.showName}
                      </h3>
                      
                      {/* Creator info */}
                 
                    </div>
                  </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{
                padding: '2rem',
                backgroundColor: '#1a1a1a',
                borderRadius: '0.5rem',
                color: '#999',
                textAlign: 'center',
                width: '100%'
              }}>
                No shows with active streaks
              </div>
            )}
          </div>
          
          {/* Right scroll button */}
          <button 
            onClick={() => scrollRight(scrollRef)}
            style={{
              position: 'absolute',
              right: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.7)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ChevronRight size={24} color="white" />
          </button>
        </div>
      </section>
    );
  };

  // Add a function to handle sign out
  const handleSignOut = () => {
    // Assuming you're using Firebase Auth
    import('firebase/auth').then(({ getAuth, signOut }) => {
      const auth = getAuth();
      signOut(auth).then(() => {
        // Sign-out successful, redirect to login page
        navigate('/login');
      }).catch((error) => {
        // An error happened
        console.error('Sign out error:', error);
      });
    });
  };

  // Add this useEffect to handle clicking outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettingsDropdown && !event.target.closest('.settings-dropdown-container')) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsDropdown]);

  // Replace the current scroll prevention approach with a better one
  useEffect(() => {
    if (activeTab) {
      // Instead of fixing the body position, just prevent the background content from scrolling
      // by adding a class to the main content container
      document.body.classList.add('no-scroll-background');
    } else {
      document.body.classList.remove('no-scroll-background');
    }
    
    return () => {
      // Clean up
      document.body.classList.remove('no-scroll-background');
    };
  }, [activeTab]);

  // Add this style to the document head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      body {
        overflow-x: hidden; /* Prevent horizontal scrollbar */
      }
      
      body.no-scroll-background {
        overflow: hidden; /* Prevent scrolling when tab is open */
      }
      
      .main-content {
        width: 100%;
        max-width: 100vw;
        overflow-x: hidden;
      }
      
      .tab-content-container {
        width: 100%;
        left: 0;
        right: 0;
        box-sizing: border-box;
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#121212'
      }}>
        <span className="loader"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#121212',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <p style={{ color: '#ff6b6b', marginBottom: '1rem' }}>Error: {error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#6600CC',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#121212',
      color: 'white',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Top Navbar */}
      <header style={{
        backgroundColor: '#6600CC',
        padding: '1rem 2rem',
        display: 'flex',
        height: '2rem',
        position: 'fixed',
        width: 'calc(100% - 4rem)',
        alignItems: 'center',
        zIndex: '100',
        justifyContent: 'space-between',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          {/* Logo/Brand */}
          <img src={SpielLogo} style={{height: '2rem'}} />
          <h1 style={{ 
            margin: 0, 
            fontSize: '1.5rem', 
            fontWeight: 'bold',
            cursor: 'pointer'
          }} onClick={() => {
            setActiveTab(null);
            navigate('/');
          }}>
            Spiel
          </h1>
          
          {/* Search Bar - Moved from main content */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: '1.5rem'
          }}>
            {/* Search input */}
            <div style={{ 
              position: 'relative',
              width: '300px'
            }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search shows..."
                className="search-input"
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem 0.5rem 2.5rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
              <Search 
                size={18} 
                style={{
                  position: 'absolute',
                  left: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255, 255, 255, 0.7)'
                }}
              />
              
              {/* Search Results Dropdown */}
              {isSearching && searchQuery && (
                <div 
                  className="search-results"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter:'blur(15px)',
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    zIndex: 10,
                    marginTop: '0.25rem',
                    // Firefox scrollbar styles
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#6600CC #333',
                  }}
                >
                  {searchResults.length > 0 ? (
                    searchResults.map(show => (
                      <div
                        key={show.id}
                        onClick={() => {
                          setSelectedShowId(show.id);
                          setSearchQuery('');
                          setIsSearching(false);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          borderBottom: '1px solid #333',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          ':hover': {
                            backgroundColor: '#222'
                          }
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem'
                        }}>
                          <div style={{
                            width: '2rem',
                            height: '3rem',
                            borderRadius: '0.25rem',
                            overflow: 'hidden',
                            backgroundColor: '#333'
                          }}>
                            {show.showImage ? (
                              <img
                                src={show.showImage}
                                alt={show.showName}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#777'
                              }}>
                                <Film size={20} />
                              </div>
                            )}
                          </div>
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{show.showName}</div>
                            <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '.3rem', border: '1px solid grey', width: '60px', textAlign: 'center', borderRadius: '.2rem' }}>
                              {show.showPossibleScore > 0 
                                ? `ISDb: ${((show.showEarnedScore / show.showPossibleScore) * 10).toFixed(1)}`
                                : 'ISDb: NR'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
                      No results found
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* User Handle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginLeft: '5rem',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}>
              <User size={16} />
              @{userData?.handle || 'username'}
            </div>
          </div>
        </div>
        
        {/* Navigation Links */}
        <nav style={{ display: 'flex', gap: '4rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            marginLeft: '2rem'
          }}>
          
           
            
            <button
              onClick={() => handleTabClick('notifications')}
              style={{
                background: 'none',
                border: 'none',
                color: activeTab === 'notifications' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.9rem',
                width: '9rem',
                borderBottom: activeTab === 'notifications' ? '2px solid rgb(255,255,255,.6)' : 'none',
                fontWeight: activeTab === 'notifications' ? 'bold' : 'normal',
                padding: '0.5rem 0',
                cursor: 'pointer',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Bell size={18} />
              <span>Notifications</span>
              {notifications.filter(n => n.status === 'pending').length > 0 && (
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontSize: '0.7rem',
                  padding: '0.1rem 0.4rem',
                  borderRadius: '1rem',
                  fontWeight: 'bold'
                }}>
                  {notifications.filter(n => n.status === 'pending').length}
                </div>
              )}
              {activeTab === 'notifications' && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  backgroundColor: '#6600CC',
                  borderRadius: '1px'
                }} />
              )}
            </button>

            <button
              onClick={() => handleTabClick('friends')}
              style={{
                background: 'none',
                border: 'none',
                color: activeTab === 'friends' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.9rem',
                
                width: '6.8rem',
                borderBottom: activeTab === 'friends' ? '2px solid rgb(255,255,255,.6)' : 'none',
                fontWeight: activeTab === 'friends' ? 'bold' : 'normal',
                padding: '0.5rem 0',
                cursor: 'pointer',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Users size={18} />
              <span>Friends</span>
              {friends.length > 0 && (
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontSize: '0.7rem',
                  padding: '0.1rem 0.4rem',
                  borderRadius: '1rem',
                  fontWeight: 'bold'
                }}>
                  {friends.length}
                </div>
              )}
              {activeTab === 'friends' && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  backgroundColor: '#6600CC',
                  borderRadius: '1px'
                }} />
              )}
            </button>
            <button
              onClick={() => handleTabClick('shows')}
              style={{
                background: 'none',
                border: 'none',
                color: activeTab === 'shows' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.9rem',
                fontWeight: activeTab === 'shows' ? 'bold' : 'normal',
                padding: '0.5rem 0',
                width: '6.5rem',
                borderBottom: activeTab === 'shows' ? '2px solid rgb(255,255,255,.6)' : 'none',
               
                cursor: 'pointer',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Tv size={18} />
              <span>Shows</span>
              {userShows.length > 0 && (
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontSize: '0.7rem',
                  padding: '0.1rem 0.4rem',
                  borderRadius: '1rem',
                  fontWeight: 'bold'
                }}>
                  {userShows.length}
                </div>
              )}
              {activeTab === 'shows' && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  backgroundColor: '#6600CC',
                  borderRadius: '1px'
                }} />
              )}
            </button>
            
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            {/* Settings icon with dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <Settings size={20} />
              </button>
              
              {/* Settings dropdown */}
              {showSettingsDropdown && (
                <div 
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    width: '180px',
                    zIndex: 100,
                    overflow: 'hidden'
                  }}
                >
                
                  
                  <div 
                    onClick={handleSignOut}
                    style={{
                      padding: '0.75rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      color: '#ff6b6b',
                      ':hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    <LogOut size={16} />
                    <span>Sign Out</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>
      
      {/* Featured Show */}
      {!activeTab && featuredShow && (
        <div style={{
          marginBottom: '2rem',
          borderRadius: '0.75rem',
          overflow: 'hidden',
          position: 'relative',
          height: '400px'
        }}>
          {/* Featured show background image - make this clickable */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            cursor: 'pointer', // Add cursor pointer to indicate it's clickable
          }}
          onClick={() => setSelectedShowId(featuredShow.id)} // Add click handler to open the show modal
          >
            {featuredShow.showImage ? (
              <img
                src={featuredShow.showImage}
                alt={featuredShow.showName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '0.75rem'
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '0.75rem'
              }}>
                <Film size={48} color="#666" />
              </div>
            )}
            
            {/* Gradient overlay */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0) 100%)',
              borderRadius: '0.75rem'
            }} />
          </div>
          
          {/* Featured show content - keep this on top and separate from the clickable background */}
          <div style={{
            position: 'relative',
            zIndex: 3,
            padding: '2rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            height: '100%',
            pointerEvents: 'none' // Make this non-clickable so clicks go through to the background
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '2rem',
              marginBottom: '3rem'
            }}>
              {/* Show details */}
              <div style={{
                flex: 1,
                color: 'white',
                paddingBottom: '2rem',
                pointerEvents: 'auto' // Re-enable pointer events for the content
              }}>
                <h1 style={{
                  margin: '0 0 0.5rem 0',
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  display: 'flex'
                }}>
                  {featuredShow.showName}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '1rem',
                    fontWeight: '600',
                    marginTop: '1.5rem',
                    fontSize: '1rem',
                  }}>
                    <span>S{featuredShow.currentSeason|| 0}</span>:
                    <span>E{featuredShow.episodeCount || 0}</span>
                  </div>
                </h1>
                
                {/* Buttons section - make sure these are clickable */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '2rem',
                  pointerEvents: 'auto' // Ensure buttons are clickable
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent click from bubbling to background
                      setSelectedShowForEpisode(featuredShow);
                      setShowCreateEpisodeModal(true);
                    }}
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.8)',
                      color: 'black',
                      border: 'none',
                      borderRadius: '0.5rem',
                      padding: '0.5rem 2.5rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '1rem', 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Plus size={18} />
                    New Episode
                  </button>

                  {/* Rating button */}
                  {featuredShow.showPossibleScore > 0 && (
                    <div style={{}}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent click from bubbling to background
                          navigate(`/shows/${featuredShow.id}`);
                        }}
                        style={{
                          backgroundColor: 'rgba(55,55,55,0.9)',
                          color: 'white',
                          border: '1px solid grey',
                          borderRadius: '0.5rem',
                          padding: '0.5rem 2.5rem',
                          fontWeight: 'bold',
                          textAlign: "center",
                          fontSize: '1rem', 
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        ISDb:
                        <span style={{ fontWeight: 'bold' }}>
                          {((featuredShow.showEarnedScore / featuredShow.showPossibleScore) * 10).toFixed(1)}
                        </span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Show description */}
                {featuredShow.showDescription && (
                  <p style={{
                    marginTop: '.5rem',
                    marginBottom: '-1rem',
                    fontSize: '1rem',
                    color: '#ccc',
                    maxWidth: '700px',
                    lineHeight: '1.5'
                  }}>
                    {featuredShow.showDescription.length > 200 
                      ? `${featuredShow.showDescription.substring(0, 200)}...` 
                      : featuredShow.showDescription}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Streak badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            background: '#6600cc',
            padding: '.5rem 2rem', 
            borderRadius: '.3rem',
            position: 'absolute',
            top: '6rem',
            right: '1rem',
            zIndex: 4,
            pointerEvents: 'auto' // Make sure this is clickable
          }}>
            {featuredShow.showStreak > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: '600',
                gap: '0.25rem'
              }}>
                {featuredShow.showStreak} Day Streak
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Tab Content */}
      {activeTab && (
        <div 
          className={`tab-content-container ${tabAnimationState}`}
          style={{
            backgroundColor: 'rgb(102, 0, 204,.9)',
            backdropFilter: 'blur(15px)',
            padding: '1.5rem',
            position: 'fixed',
            top: '4rem',
            zIndex: '10',
            borderBottom: '1px solid #333',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 4rem)',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          {renderTabContent()}
        </div>
      )}
      
      {/* Main Content */}
      <div 
        className="main-content"
        style={{
          flex: 1,
          padding: '2rem',
          margin: '0 auto',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginTop: activeTab ? '0' : '0rem'
        }}
      >
        {/* Show rows */}
        <div style={{ padding: '0 1rem' }}>
          {userShows.length > 0 && renderContinueCreatingRow(userShows, continueCreatingRef)}
          
          {/* Feed Section */}
          <section style={{ marginBottom: '2.5rem', marginTop: '-2rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <h2 style={{ 
              margin: 0,
              fontSize: '1.2rem',
              fontWeight: '500',
              color: 'gray'
              }}>
                Feed
              </h2>
            </div>
            
            <FeedDesktop user={user} />
          </section>
          {topRatedShows.length > 0 && (
            renderShowRow(
              'Critically Acclaimed', 
              topRatedShows, 
              'No rated shows yet', 
              topRatedShowsRef
            )
          )}
          
          {/* Top Shows by Streak - using the special renderer */}
          {topStreakShows.length > 0 && renderStreakShowsRow(topStreakShows, topStreakShowsRef)}
          
          
          {newEpisodeShows.length > 0 && (
            renderShowRow(
              'New Episodes', 
              newEpisodeShows, 
              'No shows with new episodes', 
              newEpisodeShowsRef
            )
          )}
          
          {newReleaseShows.length > 0 && (
            renderShowRow(
              'New Releases', 
              newReleaseShows, 
              'No new show releases', 
              newReleaseShowsRef
            )
          )}
          
          {allShows.length > 0 && (
            renderShowRow(
              'All Shows', 
              allShows, 
              'No shows available', 
              allShowsRef
            )
          )}
        </div>
      </div>
      
      {selectedShowId && (
        <ShowDesktop 
          user={user} 
          showId={selectedShowId} 
          onClose={() => setSelectedShowId(null)} 
        />
      )}
      
      {/* Create Show Modal */}
      {showCreateShowModal && (
        <CreateShowModal
          isOpen={showCreateShowModal}
          onClose={(newShow) => {
            if (newShow) {
              handleShowCreated(newShow);
            } else {
              setShowCreateShowModal(false);
            }
          }}
          userId={user?.uid}
        />
      )}

      {/* Add this to show the dropdown */}
      {showNotificationsDropdown && !activeTab && (
        <DesktopNotificationsTab 
          notifications={notifications} 
          userId={user.uid}
          isInline={true}
        />
      )}

      {showCreateEpisodeModal && selectedShowForEpisode && (
        <CreateEpisodeModal
          isOpen={showCreateEpisodeModal}
          onClose={() => setShowCreateEpisodeModal(false)}
          showId={selectedShowForEpisode.id}
          showName={selectedShowForEpisode.showName}
          userId={user.uid}
          currentSeason={selectedShowForEpisode.currentSeason || 1}
        />
      )}
    </div>
  );
};

export default HomeDesktop;